import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, AlertCircle, ExternalLink } from 'lucide-react';
import { StatusBadge } from '../../components/StatusBadge';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

export function QuotationsTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const cards = [
    { label: 'Total Penawaran', value: s.total_count || 0, accent: 'primary' },
    { label: 'Total Nilai', value: fIDRCompact(s.total_value), accent: '' },
    { label: 'Terkirim', value: s.sent_count || 0, accent: '' },
    { label: 'Disetujui', value: s.approved_count || 0, accent: 'success' },
    { label: 'Ditolak', value: s.rejected_count || 0, accent: 'danger' },
    { label: 'Kadaluarsa', value: s.expired_count || 0, accent: 'warn' },
    { label: 'Conversion Rate', value: `${(s.conversion_rate || 0).toFixed(1)}%`, accent: s.conversion_rate >= 40 ? 'success' : 'warn' },
    { label: 'Approved Belum Invoice', value: s.approved_not_invoiced_count || 0, accent: s.approved_not_invoiced_count > 0 ? 'warn' : 'success' },
  ];
  const rows = data?.rows || [];
  const ani = data?.approved_not_invoiced || [];

  return (
    <div className="space-y-4">
      <SectionHeader icon={FileText} title="Laporan Penawaran" action={<ExportButtons tab="quotations" filters={filters} />} />
      <KpiGrid items={cards} loading={loading} columns={4} />

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Detail Penawaran ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'quotation_number', label: 'No Penawaran', render: r => <span className="font-mono-num text-slate-100">{r.quotation_number}</span> },
              { key: 'quotation_date', label: 'Tgl Penawaran', render: r => <span className="text-slate-300">{fDateID(r.quotation_date)}</span> },
              { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400 truncate" title={r.venue}>{r.venue || '—'}</span> },
              { key: 'total_amount', label: 'Total', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.total_amount)}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'valid_until', label: 'Valid Until', render: r => (
                <span className="text-xs text-slate-300">
                  {fDateID(r.valid_until)}
                  {r.days_to_expire !== null && r.days_to_expire <= 3 && r.days_to_expire >= 0 && (
                    <span className="ml-1 text-amber-300 font-medium">({r.days_to_expire}d)</span>
                  )}
                </span>
              ) },
              { key: 'converted', label: 'Converted', render: r => <span className={`text-xs ${r.converted ? 'text-emerald-300' : 'text-slate-400'}`}>{r.converted ? 'Ya' : 'Tidak'}</span> },
              { key: 'invoice_number', label: 'Linked Invoice', render: r => <span className="text-xs font-mono-num text-slate-300">{r.invoice_number || '—'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${r.id}`); }} data-testid={`report-quot-detail-${r.id}`}>
                  <ExternalLink className="h-3 w-3" />
                </Button>
              ) },
            ]}
            rows={rows}
            emptyText="Tidak ada penawaran pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1" onClick={() => navigate(`/quotations/${r.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.quotation_number}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>Tgl: {fDateID(r.quotation_date)}</div>
                  <div>Valid: {fDateID(r.valid_until)}</div>
                  <div>Converted: <span className={r.converted ? 'text-emerald-300' : 'text-slate-400'}>{r.converted ? 'Ya' : 'Tidak'}</span></div>
                </div>
                <div className="font-mono-num font-semibold text-slate-100 mt-1">{fIDR(r.total_amount)}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {ani.length > 0 && (
        <Card className="bg-card border-amber-400/30">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-200 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Approved Quotation Belum Jadi Invoice ({ani.length})</CardTitle></CardHeader>
          <CardContent className="p-0 sm:p-4">
            <ReportTable
              getRowKey={(r) => `ani-${r.id}`}
              columns={[
                { key: 'quotation_number', label: 'No Penawaran', render: r => <span className="font-mono-num text-slate-100">{r.quotation_number}</span> },
                { key: 'client_name', label: 'Client', render: r => <span className="text-slate-100">{r.client_name}</span> },
                { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
                { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400" title={r.venue}>{r.venue || '—'}</span> },
                { key: 'total_amount', label: 'Total Approved', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.total_amount)}</span> },
                { key: 'action', label: 'Aksi', align: 'right', render: r => (
                  <Button size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/quotations/${r.id}`); }}>Convert</Button>
                ) },
              ]}
              rows={ani}
              emptyText="Semua approved sudah jadi invoice."
              mobileCard={(r) => (
                <div className="text-sm space-y-1" onClick={() => navigate(`/quotations/${r.id}`)}>
                  <div className="font-semibold text-slate-100">{r.client_name}</div>
                  <div className="text-xs text-slate-300 font-mono-num">{r.quotation_number}</div>
                  <div className="font-mono-num text-slate-100 mt-1">{fIDR(r.total_amount)}</div>
                </div>
              )}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
